/**
 * Infrastructure Confirmation Messages
 *
 * Provides i18n message constants and React components for displaying
 * confirmation dialogs when performing batch operations on virtual infrastructure.
 */

import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import PropTypes from 'prop-types';
import {
    HYPERVISOR_TYPE,
    VCENTER_TYPE,
    VIRTUALMACHINE_TYPE,
} from '../../filters/virtual-infrastructure-type-filter/SubTypeHelper';
import { DISABLE, ENABLE } from '../../../services/FormDataTableActionService';
import { VIRTUAL_ACTIONS } from './InfrastructureConstants';

// Enable action messages
const VCENTER_ENABLE = i18n._(
    t`Enable <b>vCenters</b> will also enable all <b>hypervisors</b> and <b>virtual machines</b> belonging to them.`
);
const HYPERVISOR_ENABLE = i18n._(
    t`Enable <b>hypervisors</b> will also enable <b>virtual machines</b> and <b>vCenters</b> they are associated with.`
);
const VIRTUALMACHINE_ENABLE = i18n._(
    t`Enable <b>virtual machines</b> will also enable <b>vCenters</b> they are associated with.`
);

// Disable action messages
const VCENTER_DISABLE = i18n._(
    t`Disable <b>vCenters</b> will also disable <b>hypervisors</b> and <b>virtual machines</b> belonging to them.`
);
const HYPERVISOR_DISABLE = i18n._(
    t`Disable <b>hypervisors</b> will also disable <b>virtual machines</b> belonging to them.`
);

// Delete action message
const VCENTER_DELETE = i18n._(
    t`Are you sure you want to delete this item? Deleting <b>vCenter</b> will delete all <b>hypervisors</b> and <b>virtual machines</b> in its scope.`
);

// Monitored infrastructure disable/delete messages
const ONE_MONITORED_DISABLE_DELETE = i18n._(
    t`This selected device is within a IP Scope with "<b>Discover devices in this scope</b>" enabled, and will be re-added to the system if discovered again. To stop monitoring IPs in a discovered IP scope, you can <b>Disable</b> monitoring instead.`
);

const ALL_MONITORED_DISABLE_DELETE = i18n._(
    t`The selected devices are within a IP Scope with "<b>Discover devices in this scope</b>" enabled, and will be re-added to the system if discovered again. To stop monitoring IPs in a discovered IP scope, you can <b>Disable</b> monitoring instead.`
);

const someMonitoredDisableDelete = (idCount, discoveredCount) =>
    i18n._(
        t`${discoveredCount} of the ${idCount} selected devices are within a IP Scope with "<b>Discover devices in this scope</b>" enabled, and will be re-added to the system if discovered again. To stop monitoring IPs in a discovered IP scope, you can <b>Disable</b> monitoring instead.`
    );

/**
 * Component to display monitored infrastructure disable/delete warning message.
 * Varies message based on how many devices are discovered vs. selected.
 */
export const MonitoredDisableDeleteMsg = ({ idCount, discoveredCount }) => {
    if (idCount === 1 && discoveredCount === 1) {
        // eslint-disable-next-line react/no-danger
        return <div dangerouslySetInnerHTML={{ __html: ONE_MONITORED_DISABLE_DELETE }} />;
    }

    if (idCount === discoveredCount) {
        // eslint-disable-next-line react/no-danger
        return <div dangerouslySetInnerHTML={{ __html: ALL_MONITORED_DISABLE_DELETE }} />;
    }

    // eslint-disable-next-line react/no-danger
    return <div dangerouslySetInnerHTML={{ __html: someMonitoredDisableDelete(idCount, discoveredCount) }} />;
};

MonitoredDisableDeleteMsg.propTypes = {
    idCount: PropTypes.number.isRequired,
    discoveredCount: PropTypes.number.isRequired,
};

/**
 * Component to display enable action confirmation message.
 * Shows different messages based on which subtype(s) are selected.
 */
const EnableConfirmMsg = ({ selectSubTypeSet }) => (
    <>
        {selectSubTypeSet.has(VCENTER_TYPE) && (
            // eslint-disable-next-line react/no-danger
            <div dangerouslySetInnerHTML={{ __html: VCENTER_ENABLE }} />
        )}
        {selectSubTypeSet.has(HYPERVISOR_TYPE) && (
            // eslint-disable-next-line react/no-danger
            <div dangerouslySetInnerHTML={{ __html: HYPERVISOR_ENABLE }} />
        )}
        {selectSubTypeSet.has(VIRTUALMACHINE_TYPE) && (
            // eslint-disable-next-line react/no-danger
            <div dangerouslySetInnerHTML={{ __html: VIRTUALMACHINE_ENABLE }} />
        )}
    </>
);

EnableConfirmMsg.propTypes = {
    selectSubTypeSet: PropTypes.instanceOf(Set).isRequired,
};

/**
 * Component to display disable action confirmation message.
 * Shows different messages based on which subtype(s) are selected.
 */
const DisableConfirmMsg = ({ selectSubTypeSet }) => (
    <>
        {selectSubTypeSet.has(VCENTER_TYPE) && (
            // eslint-disable-next-line react/no-danger
            <div dangerouslySetInnerHTML={{ __html: VCENTER_DISABLE }} />
        )}
        {selectSubTypeSet.has(HYPERVISOR_TYPE) && (
            // eslint-disable-next-line react/no-danger
            <div dangerouslySetInnerHTML={{ __html: HYPERVISOR_DISABLE }} />
        )}
    </>
);

DisableConfirmMsg.propTypes = {
    selectSubTypeSet: PropTypes.instanceOf(Set).isRequired,
};

/**
 * Component to display delete action confirmation message.
 */
const DeleteConfirmMsg = () => (
    // eslint-disable-next-line react/no-danger
    <div dangerouslySetInnerHTML={{ __html: VCENTER_DELETE }} />
);

/**
 * Main confirmation message component that delegates to appropriate sub-component
 * based on the action type (ENABLE, DISABLE, or DELETE).
 */
export const ConfirmMsg = ({ action, selectSubTypeSet }) => {
    if (action === ENABLE) {
        return <EnableConfirmMsg selectSubTypeSet={selectSubTypeSet} />;
    }

    if (action === DISABLE) {
        return <DisableConfirmMsg selectSubTypeSet={selectSubTypeSet} />;
    }

    return <DeleteConfirmMsg />;
};

ConfirmMsg.propTypes = {
    action: PropTypes.oneOf(VIRTUAL_ACTIONS).isRequired,
    selectSubTypeSet: PropTypes.instanceOf(Set).isRequired,
};
